"use client";

/**
 * The welcome card, on any page. A new account arrives with ?welcome=1
 * wherever it was going (the OAuth callback keeps the return path, One
 * Tap stays on the page it fired on); this host opens the card there,
 * names that page on the button, and drops the flag from the URL on
 * close. Until 2026-10-14 the card lived on the home page's anonymous
 * branch only, so a Google sign-up never saw it.
 *
 * In the interruption budget (lib/floating-slot) it answers the sign-up,
 * so it needs no budget: it comes up at once, even on a session's first
 * page view or over a result (the flag lives in this page's address
 * only). It still waits for the cookie note and for an offer already on
 * screen (a coach mark makes way for it), and it uses up the session, so
 * no offer follows it.
 */
import { Suspense, useEffect, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useAuth } from "@/lib/auth-context";
import { useInterruption } from "@/lib/floating-slot";
import { useLocalizedRouter } from "@/lib/localized-router";
import WelcomeModal from "@/components/WelcomeModal";

function stripLocale(path: string): string {
  return path.replace(/^\/(az|ru|en)(?=\/|$)/, "") || "/";
}

function Host() {
  const pathname = usePathname() || "/";
  const searchParams = useSearchParams();
  const router = useLocalizedRouter();
  const { user, loading } = useAuth();
  const [open, setOpen] = useState(false);
  const onScreen = useInterruption("welcome", open);
  const flagged = searchParams.get("welcome") === "1";

  useEffect(() => {
    if (flagged && !loading && user) setOpen(true);
  }, [flagged, loading, user]);

  const here = stripLocale(pathname);
  const rest = new URLSearchParams(searchParams.toString());
  rest.delete("welcome");
  const clean = `${here}${rest.toString() ? `?${rest}` : ""}`;

  const close = () => {
    setOpen(false);
    if (flagged) router.replace(clean);
  };
  if (!onScreen) return null;
  return (
    <WelcomeModal
      open={onScreen}
      onClose={close}
      onGo={(href) => {
        setOpen(false);
        router.push(href);
      }}
      destination={here}
    />
  );
}

export default function WelcomeHost() {
  return (
    <Suspense fallback={null}>
      <Host />
    </Suspense>
  );
}
